(() => {
  const docEl = document.documentElement;
  const mq = (q) => window.matchMedia(q).matches;
  const reduce = mq('(prefers-reduced-motion: reduce)');

  // Lite mode (html.mk-lite): low-end or data-saving devices get a calm, static page.
  // Any of: reduced motion, Save-Data, <= 4 GB memory, <= 4 cores, or a touch device narrower than 768px.
  const lite = (() => {
    try {
      const conn = navigator.connection || {};
      const touch = mq('(pointer: coarse)') || 'ontouchstart' in window;
      return (
        reduce ||
        conn.saveData === true ||
        (Number(navigator.deviceMemory) > 0 && navigator.deviceMemory <= 4) ||
        (Number(navigator.hardwareConcurrency) > 0 && navigator.hardwareConcurrency <= 4) ||
        (touch && window.innerWidth < 768)
      );
    } catch {
      return false;
    }
  })();
  if (lite) docEl.classList.add('mk-lite');
  window.MukhlisLite = lite;

  // Tab hidden: pause every CSS animation (html.mk-hidden in motion.css).
  document.addEventListener('visibilitychange', () => docEl.classList.toggle('mk-hidden', document.hidden));

  function ensureOrbs() {
    if (lite) return; // three blurred, animated full-screen circles are the most expensive paint on the page
    if (document.querySelector('.motion-orbs')) return;
    const wrap = document.createElement('div');
    wrap.className = 'motion-orbs';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML = '<span></span><span></span><span></span>';
    document.body.prepend(wrap);
  }

  function ensureProgress() {
    if (document.querySelector('.scroll-progress')) return;
    const bar = document.createElement('div');
    bar.className = 'scroll-progress';
    document.body.prepend(bar);
  }

  /* Infinite animations: lite → stopped (html.mk-lite .mk-still); everywhere else → paused while off-screen (.mk-off).
     Kept running: spinners, the Candles hero flame (cheap opacity flicker in lite), the Smart Cart. */
  const KEEP = '.mk-hero .mk-fl, [class*="spin"], [aria-busy="true"], [data-sc], .sc-bar';
  const governed = new WeakSet();
  let offIo = null;
  function governAnimations() {
    if (typeof document.getAnimations !== 'function') return;
    if (!lite && !offIo && 'IntersectionObserver' in window) {
      offIo = new IntersectionObserver(
        (entries) => entries.forEach((e) => e.target.classList.toggle('mk-off', !e.isIntersecting)),
        { rootMargin: '120px 0px' }
      );
    }
    let list = [];
    try {
      list = document.getAnimations();
    } catch {
      return;
    }
    list.forEach((a) => {
      const timing = a.effect && typeof a.effect.getTiming === 'function' ? a.effect.getTiming() : null;
      const el = a.effect && a.effect.target;
      if (!timing || timing.iterations !== Infinity || !el || el.nodeType !== 1 || governed.has(el)) return;
      governed.add(el);
      if (el.closest(KEEP)) return;
      if (lite) el.classList.add('mk-still');
      else if (offIo && !el.closest('.motion-orbs')) offIo.observe(el);
    });
  }

  // Story videos: preload="none" in the markup (data-mk-preload); load the first frame only when near the viewport.
  function lazyVideos() {
    const vids = document.querySelectorAll('video[data-mk-preload]');
    if (!vids.length) return;
    if (!('IntersectionObserver' in window)) {
      vids.forEach((v) => (v.preload = v.dataset.mkPreload));
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.preload = e.target.dataset.mkPreload || 'metadata';
          io.unobserve(e.target);
        }),
      { rootMargin: '300px 0px' }
    );
    vids.forEach((v) => io.observe(v));
  }

  function initReveals() {
    const nodes = document.querySelectorAll('.reveal');
    if (!nodes.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      nodes.forEach((n) => n.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    nodes.forEach((n) => io.observe(n));
  }

  function initHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const progress = document.querySelector('.scroll-progress');
    // Page height is measured on resize only, never inside the scroll handler (no forced layout per scroll).
    let max = 0;
    const measure = () => {
      max = document.documentElement.scrollHeight - window.innerHeight;
    };
    let ticking = false;
    const paint = () => {
      ticking = false;
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      if (progress) progress.style.width = `${max > 0 ? Math.min(100, (y / max) * 100) : 0}%`;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    };
    measure();
    paint();
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(document.body);
    else window.addEventListener('resize', measure);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initMobileNav() {
    // Handled by header-search.js for hamburger + overlay
  }

  function initHero() {
    const hero = document.querySelector('.hero');
    if (!hero) return;
    requestAnimationFrame(() => hero.classList.add('is-ready'));
    const media = hero.querySelector('[data-parallax]');
    if (!media || reduce || lite) return;
    window.addEventListener(
      'scroll',
      () => {
        const rect = hero.getBoundingClientRect();
        if (rect.bottom < 0) return;
        const offset = Math.min(60, Math.max(-60, -rect.top * 0.18));
        media.style.transform = `translate3d(0, ${offset}px, 0)`;
      },
      { passive: true }
    );
  }

  function initMagnetic() {
    if (reduce || window.matchMedia('(pointer: coarse)').matches) return;
    document.querySelectorAll('.btn--primary, .btn--secondary, .btn--whatsapp').forEach((btn) => {
      btn.classList.add('magnetic');
      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        btn.style.transform = `translate(${x * 0.15}px, ${y * 0.2}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  }

  function initCardTilt() {
    if (reduce || window.matchMedia('(pointer: coarse)').matches) return;
    document.querySelectorAll('.product-card').forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        const rx = (0.5 - y) * 8;
        const ry = (x - 0.5) * 10;
        card.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-6px)`;
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = '';
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    ensureOrbs();
    ensureProgress();
    initReveals();
    initHeader();
    initMobileNav();
    initHero();
    initMagnetic();
    initCardTilt();
    lazyVideos();
    // Animations can start later (lazy sections, JS-added classes): scan a few times, cheap.
    governAnimations();
    setTimeout(governAnimations, 1500);
    window.addEventListener('load', () => setTimeout(governAnimations, 300), { once: true });
    setTimeout(governAnimations, 6000);
  });
})();
